import { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Sparkles, CheckCircle, PartyPopper, X } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

interface FirstContentCelebrationProps {
  contentCount: number;
  onCelebrationComplete: () => void;
}

export const FirstContentCelebration = ({ contentCount, onCelebrationComplete }: FirstContentCelebrationProps) => {
  const { t } = useLanguage();
  const [isVisible, setIsVisible] = useState(true);
  const [animationStep, setAnimationStep] = useState<'initial' | 'celebrating' | 'complete'>('initial');

  useEffect(() => {
    // Start celebration animation
    const timer1 = setTimeout(() => {
      setAnimationStep('celebrating');
    }, 500);

    // Complete celebration
    const timer2 = setTimeout(() => {
      setAnimationStep('complete');
    }, 3000);

    // Auto-hide after 5 seconds
    const timer3 = setTimeout(() => {
      setIsVisible(false);
      onCelebrationComplete();
    }, 5000);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
    };
  }, [onCelebrationComplete]);

  const handleContinue = () => {
    setIsVisible(false);
    onCelebrationComplete();
  };

  if (!isVisible) return null;

  return (
    <div className="fixed top-4 right-4 z-50 max-w-sm">
      <Card className="relative overflow-hidden shadow-lg border-primary/20">
        {/* Close Button */}
        <Button
          variant="ghost"
          size="sm"
          className="absolute top-2 right-2 h-8 w-8 p-0 hover:bg-muted"
          onClick={handleContinue}
        >
          <X className="h-4 w-4" />
        </Button>
        
        <CardContent className="p-6 text-center">

          {/* Main Content */}
          <div className="space-y-6 relative z-10">
            {/* Icon */}
            <div className="w-20 h-20 mx-auto bg-gradient-primary rounded-full flex items-center justify-center animate-scale-in">
              {animationStep === 'initial' && (
                <Sparkles className="h-10 w-10 text-white animate-pulse" />
              )}
              {animationStep === 'celebrating' && (
                <PartyPopper className="h-10 w-10 text-white animate-bounce" />
              )}
              {animationStep === 'complete' && (
                <CheckCircle className="h-10 w-10 text-white" />
              )}
            </div>

            {/* Title */}
            <div className="space-y-2">
              <h2 className="text-2xl font-bold text-foreground animate-fade-in">
                🎉 {t('firstContent.title')}
              </h2>
              <p className="text-muted-foreground animate-fade-in">
                {t('firstContent.description').split('{{count}}')[0]}
                <span className="font-semibold text-primary">{contentCount} {contentCount === 1 ? 'pomysł' : 'pomysłów'}</span>
                {t('firstContent.description').split('{{count}}')[1]}
              </p>
            </div>

            {/* Success Message */}
            <div className="p-4 bg-success/10 rounded-lg border border-success/20 animate-fade-in">
              <p className="text-success text-sm">
                ✨ {t('firstContent.pipelineActive')}
              </p>
            </div>

            {/* Continue Button */}
            {animationStep === 'complete' && (
              <Button 
                onClick={handleContinue}
                size="lg"
                className="animate-fade-in bg-primary hover:bg-primary/90"
              >
                {t('firstContent.viewButton')}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};